import React from "react";

import { cx } from "@/lib/tailwind-utils";

interface RadioCardMultiSelectGroupProps {
  children: React.ReactNode;
  className?: string;
}

interface RadioCardMultiSelectItemProps<TValue extends string = string> {
  value: TValue;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  onToggle?: (value: TValue) => void;
}

export function RadioCardMultiSelectGroup({
  children,
  className,
}: RadioCardMultiSelectGroupProps) {
  return <div className={cx("grid gap-2", className)}>{children}</div>;
}

export function RadioCardMultiSelectItem<TValue extends string = string>({
  value,
  title,
  description,
  icon,
  selected = false,
  disabled = false,
  className,
  onToggle,
}: RadioCardMultiSelectItemProps<TValue>) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle?.(value)}
      disabled={disabled}
      className={cx(
        "group relative w-full cursor-pointer rounded-md border p-4 text-left shadow-sm transition focus:outline-none",
        selected
          ? "border-primary bg-primary-100/50 ring-2 ring-primary/60"
          : "border-border  hover:border-primary/50",
        disabled && "cursor-not-allowed opacity-60",
        className,
      )}
      aria-pressed={selected}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {icon && <div className="mt-0.5 text-2xl">{icon}</div>}
          <div>
            {title && (
              <div className="text-body-strong text-foreground">{title}</div>
            )}
            {description && (
              <div className="mt-1 text-caption text-muted-foreground">
                {description}
              </div>
            )}
          </div>
        </div>
        <div
          className={cx(
            "mt-0.5 size-2 shrink-0 rounded-full border",
            selected ? "border-primary bg-primary" : "border-border ",
          )}
          aria-hidden
        />
      </div>
    </button>
  );
}
